import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ArrowLeft, Download, Trash2, RotateCw, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;
const SCALE = 100; // pixels per inch

export default function SharedEditor() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [template, setTemplate] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 850, height: 1100 });
  const [prefixImage, setPrefixImage] = useState(null);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadingToSlot, setUploadingToSlot] = useState(null);

  useEffect(() => {
    fetchTemplate();
    fetchImages();
  }, [templateId]);

  useEffect(() => {
    if (template?.template_type === "single") {
      setCanvasSize({ width: 330, height: 550 });
    } else if (template?.template_type === "postcard" || template?.template_type === "text-postcard") {
      setCanvasSize({ width: 350, height: 650 });
    } else {
      setCanvasSize({ width: 850, height: 1100 });
    }
  }, [template?.template_type]);

  useEffect(() => {
    if (template?.template_type === "branded" && template?.prefix_image_url) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setPrefixImage(img);
      img.src = `${API}/prefix-images/${templateId}`;
    }
  }, [template?.prefix_image_url, template?.template_type, templateId]);

  useEffect(() => {
    images.forEach((img) => {
      if (!loadedImages[img.id]) {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
          setLoadedImages(prev => ({ ...prev, [img.id]: image }));
        };
        image.src = `${API}/images/${img.id}`;
      }
    });
  }, [images]);

  useEffect(() => {
    drawCanvas();
  }, [template, images, selectedImage, hoveredSlot, loadedImages, prefixImage]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}`);
      setTemplate(response.data);
    } catch (error) {
      setError("Template not found or has been deleted");
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/images`);
      setImages(response.data);
    } catch (error) {
      console.error("Failed to load images");
    }
  };

  const getShapePositions = () => {
    if (!template) return [];
    
    if (template.template_type === "branded") {
      const rectWidth = 2.25 * SCALE;
      const rectHeight = 3.25 * SCALE;
      const pageWidth = 8.5 * SCALE;
      const pageHeight = 11 * SCALE;
      const margin = 0.25 * SCALE;
      
      const cols = 3;
      const rows = 3;
      const spacingX = (pageWidth - cols * rectWidth - 2 * margin) / (cols - 1);
      const spacingY = (pageHeight - rows * rectHeight - 2 * margin) / (rows - 1);
      
      const positions = [];
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          positions.push({
            index: row * cols + col,
            x: margin + col * (rectWidth + spacingX),
            y: margin + row * (rectHeight + spacingY),
            width: rectWidth,
            height: rectHeight,
            type: "rectangle"
          });
        }
      }
      return positions;
    } else if (template.template_type === "single" || template.template_type === "event") {
      return [{
        index: 0,
        x: 0,
        y: 0,
        width: 3.3 * SCALE,
        height: 5.5 * SCALE,
        type: "rectangle"
      }];
    } else {
      // Circles
      const circleDiameter = 1.97 * SCALE;
      const margin = 0.4 * SCALE;
      const cols = Math.floor((8.5 * SCALE - 2 * margin) / circleDiameter);
      const rows = Math.floor((11 * SCALE - 2 * margin) / circleDiameter);
      
      const positions = [];
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          positions.push({
            index: row * cols + col,
            x: margin + col * circleDiameter,
            y: margin + row * circleDiameter,
            width: circleDiameter,
            height: circleDiameter,
            type: "circle"
          });
        }
      }
      return positions.slice(0, template.total_slots);
    }
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !template) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const positions = getShapePositions();
    
    positions.forEach((pos) => {
      const img = images.find(i => i.circle_index === pos.index);
      const isHovered = hoveredSlot === pos.index;
      const isBranded = template?.template_type === "branded";
      const userAreaHeight = isBranded ? pos.height * (5/6) : pos.height;  // 5/6 for photo, 1/6 for logo
      
      if (img && loadedImages[img.id]) {
        const loadedImg = loadedImages[img.id];
        
        ctx.save();
        ctx.beginPath();
        if (pos.type === "rectangle") {
          ctx.rect(pos.x, pos.y, pos.width, userAreaHeight);
        } else {
          ctx.arc(pos.x + pos.width / 2, pos.y + pos.height / 2, pos.width / 2, 0, 2 * Math.PI);
        }
        ctx.clip();
        
        const scale = Math.max(pos.width / loadedImg.width, userAreaHeight / loadedImg.height);
        const imgWidth = loadedImg.width * scale;
        const imgHeight = loadedImg.height * scale;
        const imgX = pos.x + (pos.width - imgWidth) / 2;
        const imgY = pos.y + (userAreaHeight - imgHeight) / 2;
        
        ctx.drawImage(loadedImg, imgX, imgY, imgWidth, imgHeight);
        ctx.restore();
        
        // Draw prefix for branded
        if (isBranded && prefixImage) {
          ctx.save();
          const prefixAreaY = pos.y + userAreaHeight;
          const prefixAreaHeight = pos.height - userAreaHeight;
          ctx.beginPath();
          ctx.rect(pos.x, prefixAreaY, pos.width, prefixAreaHeight);
          ctx.clip();
          
          const prefixScale = Math.max(pos.width / prefixImage.width, prefixAreaHeight / prefixImage.height);
          const prefixWidth = prefixImage.width * prefixScale;
          const prefixHeight = prefixImage.height * prefixScale;
          const prefixX = pos.x + (pos.width - prefixWidth) / 2;
          const prefixY = prefixAreaY + (prefixAreaHeight - prefixHeight) / 2;
          
          ctx.drawImage(prefixImage, prefixX, prefixY, prefixWidth, prefixHeight);
          ctx.restore();
        } else if (isBranded) {
          ctx.fillStyle = "#f3e8ff";
          ctx.fillRect(pos.x, pos.y + userAreaHeight, pos.width, pos.height - userAreaHeight);
          ctx.fillStyle = "#9333ea";
          ctx.font = "10px Inter";
          ctx.textAlign = "center";
          ctx.fillText("Logo", pos.x + pos.width / 2, pos.y + userAreaHeight + (pos.height - userAreaHeight) / 2 + 4);
        }
        
        // Selection highlight
        if (selectedImage && selectedImage.id === img.id) {
          ctx.strokeStyle = "#8b5cf6";
          ctx.lineWidth = 4;
          ctx.strokeRect(pos.x, pos.y, pos.width, pos.height);
        }
      } else {
        // Empty slot
        if (isHovered) {
          ctx.fillStyle = "rgba(139, 92, 246, 0.1)";
          ctx.fillRect(pos.x, pos.y, pos.width, userAreaHeight);
        }
        
        // Draw + icon
        const centerX = pos.x + pos.width / 2;
        const centerY = pos.y + userAreaHeight / 2;
        const plusSize = isHovered ? 30 : 20;
        
        ctx.strokeStyle = isHovered ? "#8b5cf6" : "#d1d5db";
        ctx.lineWidth = isHovered ? 4 : 3;
        ctx.lineCap = "round";
        
        ctx.beginPath();
        ctx.moveTo(centerX, centerY - plusSize / 2);
        ctx.lineTo(centerX, centerY + plusSize / 2);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(centerX - plusSize / 2, centerY);
        ctx.lineTo(centerX + plusSize / 2, centerY);
        ctx.stroke();
        
        if (isHovered) {
          ctx.fillStyle = "#8b5cf6";
          ctx.font = "bold 12px Inter";
          ctx.textAlign = "center";
          ctx.fillText("Add", centerX, centerY + plusSize + 15);
        }
        
        // Prefix area for empty slots
        if (isBranded) {
          if (prefixImage) {
            ctx.save();
            const prefixAreaY = pos.y + userAreaHeight;
            const prefixAreaHeight = pos.height - userAreaHeight;
            ctx.beginPath();
            ctx.rect(pos.x, prefixAreaY, pos.width, prefixAreaHeight);
            ctx.clip();
            
            const prefixScale = Math.max(pos.width / prefixImage.width, prefixAreaHeight / prefixImage.height);
            const prefixWidth = prefixImage.width * prefixScale;
            const prefixHeight = prefixImage.height * prefixScale;
            const prefixX = pos.x + (pos.width - prefixWidth) / 2;
            const prefixY = prefixAreaY + (prefixAreaHeight - prefixHeight) / 2;
            
            ctx.drawImage(prefixImage, prefixX, prefixY, prefixWidth, prefixHeight);
            ctx.restore();
          } else {
            ctx.fillStyle = "#f3e8ff";
            ctx.fillRect(pos.x, pos.y + userAreaHeight, pos.width, pos.height - userAreaHeight);
            ctx.fillStyle = "#9333ea";
            ctx.font = "10px Inter";
            ctx.textAlign = "center";
            ctx.fillText("Logo", pos.x + pos.width / 2, pos.y + userAreaHeight + (pos.height - userAreaHeight) / 2 + 4);
          }
        }
      }
      
      // Slot number
      ctx.fillStyle = "#666666";
      ctx.font = "10px Inter";
      ctx.textAlign = "center";
      ctx.fillText(`#${pos.index + 1}`, pos.x + pos.width / 2, pos.y + 12);
    });
  };

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    
    const positions = getShapePositions();
    
    for (const pos of positions) {
      if (x >= pos.x && x <= pos.x + pos.width && y >= pos.y && y <= pos.y + pos.height) {
        const img = images.find(img => img.circle_index === pos.index);
        if (img) {
          setSelectedImage(img);
        } else {
          setUploadingToSlot(pos.index);
          fileInputRef.current?.click();
        }
        return;
      }
    }
    setSelectedImage(null);
  };

  const handleCanvasMouseMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    
    const positions = getShapePositions();
    let found = false;
    
    for (const pos of positions) {
      if (x >= pos.x && x <= pos.x + pos.width && y >= pos.y && y <= pos.y + pos.height) {
        setHoveredSlot(pos.index);
        found = true;
        break;
      }
    }
    
    if (!found) setHoveredSlot(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_name", "Manual Upload");
      formData.append("rotation", "0");
      
      if (uploadingToSlot !== null) {
        // Upload to specific slot logic would go here
      }
      
      await axios.post(`${API}/upload/${templateId}`, formData);
      fetchImages();
      fetchTemplate();
    } catch (error) {
      alert("Failed to upload image");
    }
    
    e.target.value = '';
    setUploadingToSlot(null);
  };

  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const bulkFileInputRef = useRef(null);
  const [oneToAllFile, setOneToAllFile] = useState(null);
  const [applyingOneToAll, setApplyingOneToAll] = useState(false);
  const oneToAllInputRef = useRef(null);

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setBulkUploading(true);
    setBulkProgress({ current: 0, total: files.length });

    let successCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setBulkProgress({ current: i + 1, total: files.length });

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("user_name", `Bulk ${i + 1}`);
        formData.append("rotation", "0");

        await axios.post(`${API}/upload/${templateId}`, formData);
        successCount++;
      } catch (error) {
        console.error(`Failed to upload ${file.name}`);
      }
    }

    setBulkUploading(false);
    setBulkProgress({ current: 0, total: 0 });
    
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }

    alert(`✅ ${successCount}/${files.length} images uploaded!`);
    fetchTemplate();
    fetchImages();
  };

  // One photo to all empty slots
  const applyOneToAllSlots = async () => {
    if (!oneToAllFile) {
      alert("Please select an image first");
      return;
    }

    if (!template) return;

    const emptySlots = template.total_slots - template.filled_count;
    if (emptySlots <= 0) {
      alert("All slots are already filled. Delete some images first.");
      return;
    }

    if (!window.confirm(`Apply this image to ${emptySlots} empty slots?`)) {
      return;
    }

    setApplyingOneToAll(true);

    try {
      const formData = new FormData();
      formData.append("file", oneToAllFile);
      
      const response = await axios.post(`${API}/templates/${templateId}/bulk-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      alert(`✅ ${response.data.filled_slots} slots filled with the same image!`);
      
      setOneToAllFile(null);
      if (oneToAllInputRef.current) {
        oneToAllInputRef.current.value = '';
      }
      fetchTemplate();
      fetchImages();
    } catch (error) {
      console.error("One-to-all upload error:", error);
      alert(error.response?.data?.detail || "Failed to apply to slots");
    } finally {
      setApplyingOneToAll(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    
    try {
      await axios.delete(`${API}/images/${imageId}`);
      setSelectedImage(null);
      fetchImages();
      fetchTemplate();
    } catch (error) {
      alert("Failed to delete image");
    }
  };

  const downloadPDF = () => {
    window.open(`${API}/templates/${templateId}/pdf`, '_blank');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-xl">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">This template may have been deleted or you do not have access.</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileUpload}
      />
      <input
        type="file"
        ref={bulkFileInputRef}
        className="hidden"
        accept="image/*"
        multiple
        onChange={handleBulkUpload}
      />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-4">
        <div className="flex items-center justify-between bg-white/90 rounded-xl p-4 shadow-lg">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(`/shared/${templateId}`)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {template.template_type === "branded" ? "🏷️" : "📄"} {template.name}
              </h1>
              <p className="text-gray-600 text-sm">
                {template.template_type === "branded" ? 'Branded Card (2.25"×3.25")' : template.template_type} • {template.filled_count} photos
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {bulkUploading && (
              <div className="flex items-center gap-2 bg-purple-100 px-3 py-2 rounded-lg">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent"></div>
                <span className="text-purple-700 text-sm font-medium">
                  {bulkProgress.current}/{bulkProgress.total}
                </span>
              </div>
            )}
            <Button 
              onClick={() => bulkFileInputRef.current?.click()}
              disabled={bulkUploading}
              className="bg-purple-500 hover:bg-purple-600"
            >
              <Download className="w-4 h-4 mr-2 rotate-180" />
              Bulk Upload
            </Button>
            <Button variant="outline" onClick={() => { fetchTemplate(); fetchImages(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={downloadPDF} className="bg-green-500 hover:bg-green-600">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto flex gap-4">
        {/* Canvas */}
        <div className="flex-1 bg-white rounded-xl shadow-lg p-4 overflow-auto">
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            onClick={handleCanvasClick}
            onMouseMove={handleCanvasMouseMove}
            onMouseLeave={() => setHoveredSlot(null)}
            className="border border-gray-200 rounded-lg cursor-pointer mx-auto block"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>

        {/* Sidebar */}
        <div className="w-80 space-y-4">
          {/* Selected Image Info */}
          {selectedImage && (
            <div className="bg-white rounded-xl shadow-lg p-4">
              <h3 className="font-bold mb-3">Selected Image</h3>
              <img
                src={`${API}/images/${selectedImage.id}`}
                alt="Selected"
                className="w-full h-40 object-cover rounded-lg mb-3"
              />
              <p className="text-sm text-gray-600 mb-2">
                {selectedImage.user_name || "Unknown"}
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Slot #{selectedImage.circle_index + 1}
              </p>
              <div className="flex gap-2">
                <a
                  href={`${API}/images/${selectedImage.id}/download`}
                  download
                  className="flex-1"
                >
                  <Button variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </a>
                <Button
                  variant="outline"
                  onClick={() => deleteImage(selectedImage.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <h3 className="font-bold mb-2">Instructions</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Click empty slot (+) to add image</li>
              <li>• Click image to select & manage</li>
              <li>• Download PDF when ready to print</li>
            </ul>
          </div>

          {/* Bulk Upload Options */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <h3 className="font-bold mb-3">Bulk Upload Options</h3>
            
            {/* Multiple images */}
            <div className="mb-3 p-2 bg-purple-50 rounded-lg">
              <p className="text-xs text-purple-700 mb-2 font-medium">📤 Multiple Images</p>
              <Button
                onClick={() => bulkFileInputRef.current?.click()}
                disabled={bulkUploading}
                className="w-full bg-purple-500 hover:bg-purple-600"
                size="sm"
              >
                {bulkUploading ? `${bulkProgress.current}/${bulkProgress.total}...` : "Select Multiple"}
              </Button>
            </div>

            {/* One to all slots */}
            <div className="p-2 bg-green-50 rounded-lg">
              <p className="text-xs text-green-700 mb-2 font-medium">🔄 One Photo to All Slots</p>
              <input
                ref={oneToAllInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => setOneToAllFile(e.target.files?.[0] || null)}
                className="w-full text-xs border rounded px-2 py-1 mb-2"
              />
              {oneToAllFile && (
                <p className="text-xs text-gray-600 mb-2 truncate">Selected: {oneToAllFile.name}</p>
              )}
              <Button
                onClick={applyOneToAllSlots}
                disabled={!oneToAllFile || applyingOneToAll}
                className="w-full bg-green-500 hover:bg-green-600"
                size="sm"
              >
                {applyingOneToAll ? "Applying..." : "Fill All Empty Slots"}
              </Button>
            </div>
          </div>

          {/* Quick Upload */}
          <div className="bg-white rounded-xl shadow-lg p-4">
            <h3 className="font-bold mb-3">Quick Upload</h3>
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2 rotate-180" />
              Upload Single Image
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
